import { GridColDef } from "@mui/x-data-grid";
import { PayPeriodDto } from "../../../../models/pay-period.dto";
import { ReportType } from "../../../../models/payroll.dto";
import { formatDate, moneyFormat } from "../../../../helpers/appHelpers";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import { useGetImage } from "../../../../helpers/useGetImage";

type ReportTable = {
    title: string;
    tableData: any[];
    tableColumns: GridColDef[];
    type: ReportType;
}

type PayrollReportPDFProps = {
    reportInfo: ReportTable;
    payPeriod: PayPeriodDto;
    pensionTierName?: string;
}

const ROWS_PER_PAGE = 25;

const PayrollReportPDF = ({reportInfo, payPeriod, pensionTierName }: PayrollReportPDFProps) => {
	const { currentCompany } = useCompanyStore();
    const companyLogo = useGetImage(currentCompany?.logoUrl || "", [currentCompany]);

    // Calculate number of pages needed
    const totalPages = Math.ceil((reportInfo.tableData?.length || 0) / ROWS_PER_PAGE);

    // Function to render header section
    const renderHeader = (pageNumber: number) => (  
        <div className={`w-full flex items-center justify-between gap-[300px] ${reportInfo.title === "Salary Report" ? "mb-[100px]" : "mb-[50px]"}`}>
            <div className="flex items-center gap-2">
                {companyLogo && (
                    <img 
                        src={companyLogo}
                        alt=""
                        className="h-[80px] w-auto rounded-lg object-cover"
                    />
                )}
                <h2 className={`${reportInfo.title === "Salary Report" ? "text-6xl" : "text-5xl"} font-bold mb-6`}>{currentCompany?.name}</h2>
            </div>
            <div className="font-semibold space-y-5 text-center">
                <h2 className="text-3xl">{pensionTierName ? `${pensionTierName} Reports` : reportInfo.title}</h2>
                <p className="text-xl">Page {pageNumber} of {totalPages}</p>
            </div>
            <div className="text-center text-3xl">
                <p className="font-semibold">Period: {payPeriod?.code}</p>
                <p>({formatDate(payPeriod?.startDate)} - {formatDate(payPeriod?.endDate)})</p>
            </div>
        </div>
    );

    // Function to render table for a specific page
    const renderTable = (pageData: any[], startIndex: number, isLastPage: boolean) => (
        <table className="w-full border-collapse border border-gray-300 text-[25px]">
            <thead>
                <tr className="bg-gray-200">
                    <th key={-1} className="border border-gray-300 py-2 px-5">No</th>
                    {reportInfo.tableColumns?.map((column, index) => (
                        <th key={index} className="border border-gray-300 py-2 px-5">{column.headerName}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {pageData.map((row, index) => (
                    <tr key={startIndex + index} className="hover:bg-gray-100">
                        {(reportInfo.type === "SAVING_SCHEME") ? (
                            <>
                                <td className="border border-gray-300 py-2 px-5">{moneyFormat(startIndex + index + 1, undefined, undefined, true)}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.id}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.lastName}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.firstName}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.birthDate}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.employmentDate}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.ssn}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.basicSalary)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.employeeContribution)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.employerContribution)}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.msisdn}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.gender}</td>
                            </>
                        ) : (reportInfo.type === "SSF") ? (
                            <>
                            <td className="border border-gray-300 py-2 px-5">{moneyFormat(startIndex + index + 1, undefined, undefined, true)}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.id}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.lastName}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.firstName}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.otherNames}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.fullName}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.ssn}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.basicSalary)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.employeeContribution)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.employerContribution)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.total)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.tierAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.nationalIdNumber}</td>
                            </>
                        ) : (reportInfo.type === "BANK_REPORT") ? (
                            <>
                            <td className="border border-gray-300 py-2 px-5">{moneyFormat(startIndex + index + 1, undefined, undefined, true)}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.EMPLOYEE_CODE}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.EMPLOYEE_NAME}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.BANK_CODE}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.BANK_NAME}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.NET)}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.ACCOUNT_NUMBER}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.BANK_BRANCH_CODE}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.BANK_BRANCH_NAME}</td>
                                <td className="border border-gray-300 py-2 px-5">
                                    {row?.["PERIOD DATE"] ? new Date(row["PERIOD DATE"]).toLocaleDateString() : "--"}
                                </td>
                                <td className="border border-gray-300 py-2 px-5">{row?.REMARKS || "--"}</td>
                            </>
                        ) : (reportInfo.type === "TAX_REPORT") ? (
                            <>
                                <td className="border border-gray-300 py-2 px-5">{moneyFormat(startIndex + index + 1, undefined, undefined, true)}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.employeeTaxNumber || "--"}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.employeeName}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.position}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.residencyType}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.basicSalaryAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.isSecondaryEmployment ? 'Yes' : 'No'}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.paidSSNIT ? 'Yes' : 'No'}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.socialSecurityAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.thirdTier)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.allowanceAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.bonusAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.bonusTaxAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.excessBonusAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.totalCashEmolument)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.benefitInKindAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.totalAssessableIncomeAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.deductibleReliefAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.totalReliefAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.chargeableIncomeAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.taxDeductibleAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.overtimeIncomeAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.overtimeTaxAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.totalTaxPayableAmount)}</td>
                            </>

                        ) : (
                            <>
                                <td className="border border-gray-300 py-2 px-5">
                                    {moneyFormat(startIndex + index + 1, undefined, undefined, true)}
                                </td>
                                <td className="border border-gray-300 py-2 px-5">{row?.employeeCode}</td>
                                <td className="border border-gray-300 py-2 px-5">{row?.employeeName}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.basicPayAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.allowanceAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.grossAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.ssfEmployeeAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.taxableAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.incomeTaxAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.savingSchemesAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.otherDeductionsAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.loansAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.totalDeductionsAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.bonusAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.primaryPayAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.secondaryPayAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.deductibleReliefAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.totalReliefAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.explicitDeductionAmount)}</td>
                                <td className="border border-gray-300 py-2 px-5 text-end">{moneyFormat(row?.netAmount)}</td>

                            </>
                        )}
                    </tr>
                ))}

                {isLastPage && (
                    <tr key="totals" className="hover:bg-gray-100 font-bold">
                        {(reportInfo.type === "SAVING_SCHEME") ? (
                            <>
                                <td colSpan={7} className="border border-gray-300 py-2 px-5 text-end font-bold">
                                    Totals:
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end font-bold">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "basicSalary"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end font-bold">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "employeeContribution"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end font-bold">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "employerContribution"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5"></td>

                                <td colSpan={2} className="border border-gray-300 py-2 px-5"></td>
                            </>
                        ) : (reportInfo.type === "SSF") ? (
                            <>
                                <td colSpan={7} className="border border-gray-300 py-2 px-5 text-end font-bold">
                                    Totals
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "basicSalary"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "employeeContribution"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "employerContribution"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "total"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "tierAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5"></td>
                            </>

                            ): (reportInfo.type === "BANK_REPORT") ? (
                            <>
                                <td colSpan={2} className="border border-gray-300 py-2 px-5"></td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(reportInfo.tableData.length, undefined, undefined, true)}
                                </td>
                                <td colSpan={2} className="border border-gray-300 py-2 px-5"></td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "NET"))}
                                </td>
                                <td colSpan={3} className="border border-gray-300 py-2 px-5"></td>
                            </>
                        ): (reportInfo.type === "TAX_REPORT") ? (
                            <>
                                <td className="border border-gray-300 py-2 px-5"></td>
                                <td className="border border-gray-300 py-2 px-5"></td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(reportInfo.tableData.length, undefined, undefined, true)}
                                </td>
                                <td colSpan={2} className="border border-gray-300 py-2 px-5"></td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "basicSalaryAmount"))}
                                </td>
                                <td colSpan={2} className="border border-gray-300 py-2 px-5"></td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "socialSecurityAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "thirdTier"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "allowanceAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "bonusAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "bonusTaxAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "excessBonusAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "totalCashEmolument"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "benefitInKindAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "totalAssessableIncomeAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "totalReliefAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "totalReliefAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "chargeableIncomeAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "taxDeductibleAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "overtimeIncomeAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "overtimeTaxAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "totalTaxPayableAmount"))}
                                </td>
                            </>

                        ) : (
                            <>
                                <td colSpan={2} className="border border-gray-300 py-2 px-5"></td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(reportInfo.tableData.length, undefined, undefined, true)}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "basicPayAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "allowanceAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "grossAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "ssfEmployeeAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "taxableAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "incomeTaxAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "savingSchemesAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "otherDeductionsAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "loansAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "totalDeductionsAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "bonusAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "primaryPayAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "secondaryPayAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "deductibleReliefAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "totalReliefAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "explicitDeductionAmount"))}
                                </td>
                                <td className="border border-gray-300 py-2 px-5 text-end">
                                    {moneyFormat(calculateTotals(reportInfo.tableData, "netAmount"))}
                                </td>

                            </>
                        )}
                    </tr>
                )}
            </tbody>
        </table>
    );

    return (
        <>
            {Array.from({length: totalPages}).map((_, pageIndex) => {
                const startIndex = pageIndex * ROWS_PER_PAGE;
                const pageData = reportInfo.tableData?.slice(
                    startIndex,
                    startIndex + ROWS_PER_PAGE
                ) || [];

                return (
                    <div
                        key={pageIndex}
                        id={`pdf-content-page-${pageIndex}`}
                        className="fixed -top-[300%] -left-[500%] z-[0] bg-white px-[80px] py-[30px] w-dvw mx-auto text-black whitespac e-nowrap"
                    >
                        {renderHeader(pageIndex + 1)}
                        {renderTable(pageData, startIndex, pageIndex === totalPages - 1)}
                    </div>
                );
            })}
        </>
    );
};

export default PayrollReportPDF;

const calculateTotals = (data: any[], field: string) => {
    return data.reduce((acc, row) => {
        acc += row[field];
        return acc;
    }, 0);
};
